import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, DataTable, Dialog, Input, PageHead, Panel, type Column } from "@guestpilot/ui-kit";
import { useGetPages, type Schema } from "../api";
import { formatDateTime } from "../format";

type Event = Schema<"StaffAuditEvent">;

/** Every console write, immutable and read-only here (BR-254): who, what, on which target, before and after. */
export function AuditLog() {
  const [params, setParams] = useSearchParams();
  const get = (k: string) => params.get(k) ?? "";
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };
  const query = {
    ...(get("action") ? { action: get("action") } : {}),
    ...(get("organizationId") ? { organizationId: get("organizationId") } : {}),
    ...(get("from") ? { from: `${get("from")}T00:00:00.000Z` } : {}),
    ...(get("to") ? { to: `${get("to")}T23:59:59.999Z` } : {}),
  };
  const list = useGetPages<Event>("/audit-log", query);
  const [open, setOpen] = useState<Event | null>(null);
  const columns: Column<Event>[] = [
    { key: "t", header: "When", render: (e) => formatDateTime(e.occurredAt) },
    { key: "s", header: "Staff", render: (e) => e.staff?.name },
    { key: "a", header: "Action", render: (e) => <span className="t-mono">{e.action}</span> },
    { key: "g", header: "Target", render: (e) => `${e.targetType} · ${e.targetId}` },
    {
      key: "d",
      header: "Details",
      render: (e) => (
        <Button variant="ghost" aria-label={`Details of ${e.action}`} onClick={() => setOpen(e)}>
          Before and after
        </Button>
      ),
    },
  ];
  return (
    <div className="gp-page">
      <PageHead title="Audit log" subtitle="Every change made in this console." />
      <div className="gp-row">
        <Input
          type="search"
          aria-label="Action"
          placeholder="Action, e.g. customer.suspend"
          value={get("action")}
          onChange={(e) => set("action", e.target.value)}
        />
        <Input
          type="date"
          aria-label="From"
          value={get("from")}
          onChange={(e) => set("from", e.target.value)}
        />
        <Input type="date" aria-label="To" value={get("to")} onChange={(e) => set("to", e.target.value)} />
        {get("organizationId") ? (
          <Button variant="ghost" onClick={() => set("organizationId", "")}>
            Clear customer filter
          </Button>
        ) : null}
      </div>
      <Panel label="Audit log">
        <DataTable
          caption="Audit log"
          columns={columns}
          rows={list.items}
          loading={list.isLoading}
          error={list.isError}
          onRetry={() => void list.refetch()}
          errorTitle="Could not load the audit log."
          retryLabel="Retry"
          rowKey={(e) => e.id ?? ""}
          emptyTitle="No events match these filters"
        />
        {list.hasNextPage ? (
          <div className="gp-actions">
            <Button onClick={() => void list.fetchNextPage()}>Load more</Button>
          </div>
        ) : null}
      </Panel>
      {open ? (
        <Dialog
          open
          title={open.action ?? "Event"}
          description={`${open.staff?.name} · ${formatDateTime(open.occurredAt)} · correlation ${open.correlationId}`}
          size="lg"
          onClose={() => setOpen(null)}
          footer={<Button onClick={() => setOpen(null)}>Close</Button>}
        >
          <div className="gp-grid-2">
            <div>
              <h3 className="t-body-strong">Before</h3>
              <pre className="t-mono t-body-sm">{JSON.stringify(open.before ?? null, null, 2)}</pre>
            </div>
            <div>
              <h3 className="t-body-strong">After</h3>
              <pre className="t-mono t-body-sm">{JSON.stringify(open.after ?? null, null, 2)}</pre>
            </div>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}
